/**
 * SaveAsTemplateDialog - Dialog pentru salvarea rapidă a conținutului ca template
 * 
 * Folosit de butonul "Salvează ca Template" din ReportEditor
 * 
 * Features:
 * - Input pentru nume template
 * - Checkbox pentru adăugare la favorite
 * - Validare simplă
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Box,
  Typography,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Save as SaveIcon,
  Article as TemplateIcon
} from '@mui/icons-material';
import { doctorTemplatesApi } from '../../services/doctorTemplatesApi';
import toast from 'react-hot-toast';

interface SaveAsTemplateDialogProps {
  open: boolean;
  onClose: () => void;
  contentHtml: string;
  contentJson: any;
  onSaved?: () => void;
  suggestedName?: string;
}

const SaveAsTemplateDialog: React.FC<SaveAsTemplateDialogProps> = ({
  open,
  onClose,
  contentHtml,
  contentJson,
  onSaved,
  suggestedName = ''
}) => {
  const [name, setName] = useState(suggestedName);
  const [isFavorite, setIsFavorite] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Reset form when dialog opens
  React.useEffect(() => {
    if (open) {
      setName(suggestedName);
      setIsFavorite(false);
      setError(null);
    }
  }, [open, suggestedName]);
  
  // Extract Quill Delta from editor JSON (handles multi-segment structure)
  const extractQuillDelta = (json: any): any => {
    if (!json) return { ops: [] };
    
    // If it's already a valid Quill Delta format
    if (json.ops && Array.isArray(json.ops)) {
      return json;
    }
    
    // If it's multi-segment structure {segments: {CODE: {content_json: {...}}}}
    if (json.segments && typeof json.segments === 'object') {
      const segmentKeys = Object.keys(json.segments);
      if (segmentKeys.length > 0) {
        const firstSegment = json.segments[segmentKeys[0]];
        if (firstSegment?.content_json?.ops) {
          return firstSegment.content_json;
        }
      }
    }
    
    // Fallback
    return { ops: [] };
  };
  
  const handleSave = async () => {
    // Validare
    if (!name.trim()) {
      setError('Te rog introdu un nume pentru template');
      return;
    }
    
    if (!contentHtml || contentHtml.trim() === '' || contentHtml === '<p><br></p>') {
      setError('Conținutul editorului este gol');
      return;
    }
    
    setSaving(true);
    setError(null);
    
    // Extract proper Quill Delta from editor JSON
    const quillDelta = extractQuillDelta(contentJson);
    
    try {
      await doctorTemplatesApi.quickSave({
        name: name.trim(),
        content_html: contentHtml,
        content_json: quillDelta,
        is_favorite: isFavorite
      });
      
      toast.success(`Template "${name}" salvat cu succes!`);
      
      if (onSaved) {
        onSaved();
      }
      
      handleClose();
    } catch (err: any) {
      console.error('Error saving template:', err);
      setError(err.response?.data?.detail || 'Eroare la salvarea template-ului');
    } finally {
      setSaving(false);
    }
  };
  
  const handleClose = () => {
    setName('');
    setIsFavorite(false);
    setError(null);
    onClose();
  };
  
  // Compact dialog styles
  const dialogPaperSx = {
    bgcolor: 'background.paper',
    backgroundImage: 'none',
    '& .MuiInputLabel-root': { color: 'text.secondary', fontSize: '0.85rem' },
    '& .MuiInputLabel-root.Mui-focused': { color: 'primary.main' },
    '& .MuiOutlinedInput-root': {
      fontSize: '0.875rem',
      '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
    },
  };
  const inputSx = { '& .MuiInputBase-root': { fontSize: '0.8rem' }, '& .MuiInputLabel-root': { fontSize: '0.75rem' } };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth PaperProps={{ sx: dialogPaperSx }}>
      <DialogTitle sx={{ py: 1.5, px: 2, fontSize: '1rem', fontWeight: 600, borderBottom: '1px solid', borderColor: 'divider', color: 'text.primary' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <TemplateIcon sx={{ fontSize: 20 }} />
          <Typography sx={{ fontSize: '1rem', fontWeight: 600 }}>Salvează ca Template</Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ p: 2 }}>
        <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', mb: 1.5 }}>Salvează conținutul curent din editor ca template pentru utilizare ulterioară.</Typography>
        
        {error && <Alert severity="error" sx={{ mb: 1.5, py: 0.25, '& .MuiAlert-message': { fontSize: '0.75rem' } }}>{error}</Alert>}
        
        <TextField autoFocus label="Nume Template" placeholder="ex: CT_TAP, MR_COLOANA" value={name} onChange={(e) => setName(e.target.value)} fullWidth required size="small" sx={{ ...inputSx, mb: 1.5 }} helperText="Alege un nume descriptiv pe care îl vei recunoaște ușor" />
        
        <FormControlLabel control={<Checkbox checked={isFavorite} onChange={(e) => setIsFavorite(e.target.checked)} size="small" />} label={<Typography sx={{ fontSize: '0.78rem' }}>Adaugă la favorite (pentru acces rapid)</Typography>} />
      </DialogContent>
      
      <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button onClick={handleClose} disabled={saving} size="small" sx={{ fontSize: '0.78rem', textTransform: 'none' }}>Anulează</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving || !name.trim()} startIcon={saving ? <CircularProgress size={14} /> : <SaveIcon sx={{ fontSize: 16 }} />} size="small" sx={{ fontSize: '0.78rem', textTransform: 'none' }}>{saving ? 'Se salvează...' : 'Salvează Template'}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default SaveAsTemplateDialog;

